import React from 'react';
import { Link } from 'react-router-dom';
import { RESPONSABLE, ULTIMA_ACTUALIZACION } from '../data/legal';

/**
 * Política de Privacidad.
 *
 * Está escrita contra lo que el código hace hoy, no contra un modelo genérico.
 * Cada tercero de la tabla recibe datos de verdad y se puede señalar dónde:
 * Firebase en config/firebase.js, Mercado Pago y Google Calendar en los OAuth
 * de server.js, Resend en emailService.js, OpenAI en el asistente de WhatsApp,
 * 360dialog en el webhook, y la IP en routes/spamProtection.js.
 *
 * Dos decisiones que conviene no deshacer sin pensarlo:
 *
 * 1. Distingue los dos grupos de personas cuyos datos pasan por acá. El texto
 *    anterior hablaba sólo de "sus datos" dirigiéndose al dueño del negocio, y
 *    se olvidaba de quien reserva un turno — que no tiene cuenta, nunca aceptó
 *    nada y sin embargo deja nombre, teléfono y email. Es la mayoría de la
 *    gente que aparece en la base.
 *
 * 2. No promete controles de seguridad que no existen. El texto anterior
 *    afirmaba "auditorías de seguridad periódicas" y "copias de seguridad
 *    regulares y cifradas". No hay auditorías, y las copias son las que hace
 *    Firestore por su cuenta. Prometer un control que no está es peor que no
 *    tenerlo: convierte un problema técnico en uno legal.
 *
 * El contenido editable (titular, contacto, fecha) vive en data/legal.js para
 * que se toque en un solo lugar y no en dos documentos que se desincronizan.
 */

const TERCEROS = [
  {
    quien: 'Google (Firebase)',
    para: 'Cuentas y contraseñas, base de datos, archivos que subís (logo, fotos), métricas de uso y protección anti-abuso (App Check y reCAPTCHA).',
    donde: 'Estados Unidos',
  },
  {
    quien: 'Render',
    para: 'Servidor donde corre la aplicación que atiende los pedidos.',
    donde: 'Estados Unidos',
  },
  {
    quien: 'Mercado Pago',
    para: 'Cobro de la suscripción del negocio a Aturno, y cobro de las señas que cada negocio le hace a sus clientes con su propia cuenta.',
    donde: 'Argentina',
  },
  {
    quien: 'Google Calendar',
    para: 'Copiar los turnos a la agenda del negocio. Sólo si el negocio conecta su cuenta.',
    donde: 'Estados Unidos',
  },
  {
    quien: 'Resend',
    para: 'Envío de los correos de confirmación y recordatorio de turno.',
    donde: 'Estados Unidos',
  },
  {
    quien: 'OpenAI',
    para: 'Redactar las respuestas del asistente de WhatsApp. Recibe el contenido de esas conversaciones. Sólo si el negocio activa el asistente.',
    donde: 'Estados Unidos',
  },
  {
    quien: '360dialog / Meta',
    para: 'Enviar y recibir los mensajes de WhatsApp del asistente. Sólo si el negocio lo activa.',
    donde: 'Alemania / Estados Unidos',
  },
];

const PrivacyPolicy = () => (
  <article className="legal-documento">
    <h1 className="legal-titulo">Política de Privacidad</h1>
    <p className="legal-actualizado">Última actualización: {ULTIMA_ACTUALIZACION}</p>

    <section className="legal-resumen">
      <h2>En resumen</h2>
      <ul>
        <li>Pedimos los datos necesarios para que el negocio pueda atender el turno: nada más.</li>
        <li>No vendemos ni alquilamos datos a nadie, y no mandamos publicidad.</li>
        <li>Sólo te escribimos por WhatsApp si lo pediste marcando la casilla, que viene desmarcada.</li>
        <li>Podés pedirnos ver, corregir o borrar tus datos escribiendo a {RESPONSABLE.email}.</li>
      </ul>
    </section>

    <section className="legal-seccion">
      <h2>1. Quién es responsable de tus datos</h2>
      <p>
        Aturno es una plataforma de gestión de turnos operada por{' '}
        <strong>{RESPONSABLE.nombre}</strong>, con domicilio en {RESPONSABLE.domicilio}.
        Podés escribirnos a <a href={`mailto:${RESPONSABLE.email}`}>{RESPONSABLE.email}</a>.
      </p>
      <p>
        Hay que distinguir dos situaciones, porque la responsabilidad no es la misma:
      </p>
      <ul>
        <li>
          <strong>Si abriste una cuenta en Aturno</strong> (sos un negocio), nosotros somos
          responsables de los datos de tu cuenta.
        </li>
        <li>
          <strong>Si reservaste un turno en la página de un negocio</strong>, el responsable de
          esos datos es ese negocio: él decide qué te pide y para qué lo usa. Aturno los trata
          por cuenta de él, como prestador del servicio, y no los usa para fines propios.
        </li>
      </ul>
      <div className="legal-aviso">
        <p>
          Si reservaste un turno y querés acceder, corregir o eliminar tus datos, lo más rápido es
          pedírselo al negocio donde reservaste. Si no obtenés respuesta, escribinos a{' '}
          {RESPONSABLE.email} y te ayudamos.
        </p>
      </div>
    </section>

    <section className="legal-seccion">
      <h2>2. Qué datos tratamos</h2>

      <h3>Si abrís una cuenta como negocio</h3>
      <ul>
        <li>Correo electrónico y contraseña. La contraseña la gestiona Firebase Authentication: nosotros no la vemos ni la guardamos.</li>
        <li>Datos del negocio: nombre, rubro, dirección, teléfono, descripción, logo y colores.</li>
        <li>Cómo trabajás: horarios, servicios, precios, personal y lugares.</li>
        <li>Datos bancarios que cargues para recibir señas por transferencia: CBU o alias, titular y banco.</li>
        <li>Las autorizaciones de Mercado Pago y Google Calendar, si los conectás.</li>
        <li>Tu plan, el estado de tu suscripción y la fecha de fin de la prueba gratuita.</li>
      </ul>

      <h3>Si reservás un turno</h3>
      <p>No hace falta crear una cuenta para reservar. Se guarda:</p>
      <ul>
        <li>Tu nombre, tu teléfono y, si lo dejás, tu correo electrónico.</li>
        <li>El turno: servicio, fecha, hora, profesional y lugar, y el código de reserva.</li>
        <li>Si aceptaste recibir el recordatorio por WhatsApp. <strong>Esa casilla viene desmarcada</strong> y sin marcarla no te escribimos por ese medio.</li>
        <li>Si el servicio pide seña: el método elegido, el monto y el estado del pago.</li>
        <li>Tu dirección IP en el momento de reservar, para evitar reservas automatizadas y abuso.</li>
      </ul>
      <p>
        No pedimos ni guardamos los datos de tu tarjeta. Cuando pagás una seña, el pago ocurre
        dentro de Mercado Pago y nosotros sólo recibimos el aviso de si se aprobó o no.
      </p>
    </section>

    <section className="legal-seccion">
      <h2>3. Para qué los usamos</h2>
      <ul>
        <li>Mostrar la disponibilidad, tomar el turno y avisarle al negocio.</li>
        <li>Mandar la confirmación y el recordatorio del turno.</li>
        <li>Cobrar la seña, cuando el servicio la pide.</li>
        <li>Cobrar la suscripción del negocio y aplicar los límites de su plan.</li>
        <li>Copiar el turno a Google Calendar, si el negocio conectó su cuenta.</li>
        <li>Mantener el servicio en pie y frenar el abuso.</li>
        <li>Cumplir con las obligaciones legales y fiscales que correspondan.</li>
      </ul>
      <p>
        <strong>No usamos tus datos para publicidad</strong>, ni te mandamos novedades ni
        promociones que no hayas pedido, ni se los vendemos o alquilamos a nadie.
      </p>
    </section>

    <section className="legal-seccion">
      <h2>4. Con quién los compartimos</h2>
      <p>
        Aturno funciona apoyándose en servicios de terceros. Cada uno recibe solamente lo que
        necesita para su función:
      </p>
      <div className="legal-tabla-contenedor">
        <table className="legal-tabla">
          <thead>
            <tr>
              <th>Servicio</th>
              <th>Para qué</th>
              <th>Dónde procesa</th>
            </tr>
          </thead>
          <tbody>
            {TERCEROS.map((t) => (
              <tr key={t.quien}>
                <td><strong>{t.quien}</strong></td>
                <td>{t.para}</td>
                <td>{t.donde}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        También podemos entregar datos si nos lo exige una autoridad competente por una vía legal
        válida. Fuera de estos casos, no compartimos datos con nadie.
      </p>

      <h3>Datos que salen del país</h3>
      <p>
        Como se ve en la tabla, buena parte de la infraestructura está fuera de Argentina. Al usar
        Aturno, aceptás que tus datos se almacenen y procesen en esos países, que pueden tener
        reglas de protección distintas de las argentinas.
      </p>
    </section>

    <section className="legal-seccion">
      <h2>5. El asistente de WhatsApp</h2>
      <p>
        Algunos negocios activan un asistente que responde por WhatsApp. Si le escribís a un
        negocio que lo tiene activo, <strong>el contenido de esa conversación se envía a OpenAI</strong>{' '}
        para generar la respuesta, y pasa por 360dialog o por la API de Meta para llegar a
        WhatsApp. Tené eso en cuenta antes de escribir información delicada por ese canal.
      </p>
      <p>
        Es distinto del recordatorio de turno por WhatsApp, que requiere que hayas marcado la
        casilla al reservar.
      </p>
    </section>

    <section className="legal-seccion">
      <h2>6. Cuánto tiempo los guardamos</h2>
      <ul>
        <li>Los datos de la cuenta, mientras la cuenta exista.</li>
        <li>Los turnos, mientras el negocio los conserve, porque son su registro de actividad.</li>
        <li>Los registros de pago, por el plazo que exige la normativa fiscal y contable.</li>
        <li>Los registros técnicos y de seguridad, por un plazo acotado y para ese solo fin.</li>
      </ul>
      <p>
        Si cerrás tu cuenta, eliminamos o anonimizamos lo que no estemos obligados a conservar.
      </p>
    </section>

    <section className="legal-seccion">
      <h2>7. Cómo protegemos los datos</h2>
      <p>Lo que efectivamente hacemos hoy:</p>
      <ul>
        <li>Todo el tráfico viaja cifrado por HTTPS.</li>
        <li>Las contraseñas las administra Firebase Authentication; no las almacenamos ni podemos verlas.</li>
        <li>Cada pedido a la API se valida contra la identidad de quien lo hace, y la base tiene reglas de acceso restrictivas.</li>
        <li>Usamos App Check y reCAPTCHA para frenar el uso automatizado, y límites de frecuencia sobre los pedidos.</li>
      </ul>
      <div className="legal-aviso">
        <p>
          Ningún sistema es completamente seguro y no podemos garantizar que nunca vaya a haber un
          incidente. Si detectamos uno que afecte tus datos, te lo vamos a informar y a comunicar a
          la autoridad de control cuando corresponda.
        </p>
      </div>
    </section>

    <section className="legal-seccion">
      <h2>8. Tus derechos</h2>
      <p>
        Podés pedirnos acceder a tus datos, corregirlos si están mal, actualizarlos, pedir que los
        eliminemos y retirar el consentimiento que hayas dado. Para hacerlo, escribinos a{' '}
        <a href={`mailto:${RESPONSABLE.email}`}>{RESPONSABLE.email}</a> desde la dirección
        asociada a tus datos, o contanos con qué negocio reservaste y con qué código.
      </p>
      <p>
        El titular de los datos personales tiene la facultad de ejercer el derecho de acceso a los
        mismos en forma gratuita a intervalos no inferiores a seis meses, salvo que se acredite un
        interés legítimo al efecto, conforme lo establecido en el artículo 14, inciso 3 de la Ley
        N° 25.326.
      </p>
      <p>
        La Agencia de Acceso a la Información Pública, órgano de control de la Ley N° 25.326, tiene
        la atribución de atender las denuncias y reclamos que se interpongan con relación al
        incumplimiento de las normas sobre protección de datos personales.
      </p>
    </section>

    <section className="legal-seccion">
      <h2>9. Menores de edad</h2>
      <p>
        Para abrir una cuenta de negocio en Aturno hay que ser mayor de edad. Un menor puede
        reservar un turno con autorización de quien esté a su cargo, que es responsable de esa
        reserva.
      </p>
    </section>

    <section className="legal-seccion">
      <h2>10. Cambios en esta política</h2>
      <p>
        Si cambiamos esta política, actualizamos la fecha del encabezado y publicamos la versión
        nueva acá. Si el cambio es importante, además lo avisamos dentro de la aplicación.
      </p>
    </section>

    <section className="legal-seccion">
      <h2>11. Ley aplicable</h2>
      <p>
        Esta política se rige por la Ley N° 25.326 de Protección de los Datos Personales de la
        República Argentina y sus normas complementarias. Ante cualquier conflicto, intervienen los
        tribunales ordinarios de la Ciudad Autónoma de Buenos Aires.
      </p>
      <p>
        Podés leer también nuestros <Link to="/legal/terms">Términos y Condiciones</Link>.
      </p>
    </section>
  </article>
);

export default PrivacyPolicy;
